// Users — staff + customer role management. Inline role dropdown + active
// checkbox per row. Self-edit is blocked (server-side) to avoid lockout.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'

interface User {
  id: string; email: string; firstName: string | null; lastName: string | null
  role: string; isActive: boolean; companyId: string | null
  lastLoginAt: string | null; createdAt: string
}

interface Company { id: string; name: string }

export default function Users() {
  const { getAccessToken } = useAuth()
  const [rows, setRows] = useState<User[]>([])
  const [roles, setRoles] = useState<string[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function load() {
    try {
      const qs = new URLSearchParams()
      if (search) qs.set('search', search)
      if (roleFilter) qs.set('role', roleFilter)
      const d = await apiFetch<{ items: User[]; roles: string[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/users?${qs.toString()}`)
      setRows(d.items); setRoles(d.roles); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter])

  // Load companies once for the per-row selector. The list is small (B2B
  // customer companies — handful at most) so we fetch the full set; if it
  // grows we can switch to a typeahead.
  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ items: Company[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/companies')
        setCompanies(d.items)
      } catch { /* non-blocking — the selector falls back to no options */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function patch(id: string, body: Partial<User>) {
    setBusyId(id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusyId(null) }
  }

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Settings</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Users</h1>
          <p className="text-ink-2 text-sm mt-1">Staff + customers. New users self-onboard via WorkOS sign-in; roles can be promoted/demoted here.</p>
        </div>
        <div className="flex gap-2">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="input max-w-[160px]">
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="input max-w-xs" />
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Name</th>
              <th className="text-left px-4 py-2 font-bold">Email</th>
              <th className="text-left px-4 py-2 font-bold">Role</th>
              <th className="text-left px-4 py-2 font-bold">Company</th>
              <th className="text-left px-4 py-2 font-bold">Active</th>
              <th className="text-right px-4 py-2 font-bold">Last login</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No users match.</td></tr>}
            {rows.map((u) => (
              <tr key={u.id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3 text-ink">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                <td className="px-4 py-3 text-ink-2">{u.email}</td>
                <td className="px-4 py-3">
                  <select disabled={busyId === u.id} value={u.role} onChange={(e) => patch(u.id, { role: e.target.value })}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-border bg-white text-ink-2 focus:outline-none focus:border-gold disabled:opacity-50">
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select disabled={busyId === u.id} value={u.companyId ?? ''}
                    onChange={(e) => patch(u.id, { companyId: e.target.value || null })}
                    className="text-xs px-2 py-1 rounded border border-border bg-white text-ink-2 focus:outline-none focus:border-gold disabled:opacity-50 max-w-[180px]">
                    <option value="">— None —</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 text-xs text-ink-2">
                    <input type="checkbox" disabled={busyId === u.id} checked={u.isActive} onChange={(e) => patch(u.id, { isActive: e.target.checked })} />
                    {u.isActive ? 'enabled' : 'disabled'}
                  </label>
                </td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
